import React, { useState } from "react";
import { useCart } from "../CartContext";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PayPalButtons } from "@paypal/react-paypal-js";

const CheckoutPage = () => {
    const { cart, clearCart } = useCart();
    const navigate = useNavigate();
    const totalPrice = cart.reduce(
        (total, item) => total + item.variant.price * item.quantity,
        0
    );

    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phoneNumber: "",
        location: "",
        pincode: "",
    });

    const [isPaid, setIsPaid] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePlaceOrder = async () => {
        try {
            const orderData = {
                formData,
                orderCart: cart,
                totalPrice,
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/place-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderData),
            });

            if (response.ok) {
                alert("Order placed successfully! Check your email.");
                localStorage.removeItem("checkoutAllowed");  // Remove access after order
                navigate("/order-confirmation", { state: { cart: cart } });
                clearCart();
            } else {
                alert("Failed to send mails. Try again!");
            }
        } catch (error) {
            console.error("Error placing order: ", error);
            alert("Failed to place order. Please try again.");
        }
    };

    const handleCheckout = async (e) => {
        e.preventDefault();
        if (!isPaid) {
            alert("Please complete the PayPal payment first!");
            return;
        }

        await handlePlaceOrder();
    };

    const handleApprove = (data, actions) => {
        return actions.order.capture().then(() => {
            alert("Payment Successful!");
            setIsPaid(true);
        });
    };

    return (
        <div className="min-h-screen flex flex-col bg-black text-rose-300">
            <Header />

            {/* Main Container */}
            <div className="flex-grow pt-24 pb-12 flex items-center justify-center px-4 sm:px-8">
                <div className="w-full max-w-5xl bg-opacity-10 backdrop-blur-md border border-rose-300 p-4 sm:p-8 rounded-lg shadow-lg glass">
                    <h2
                        className="text-3xl sm:text-4xl font-bold mb-8 text-center"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                        Checkout
                    </h2>

                    {/* Checkout Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Order Summary */}
                        <div className="space-y-4">
                            <h3 className="text-2xl font-semibold mb-4">Order Summary</h3>
                            <div className="space-y-4 max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-rose-300 pr-2">
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center border-b border-rose-300 pb-4"
                                    >
                                        <img
                                            src={item.variant.image_url}
                                            alt={item.variant.color || item.variant.model}
                                            className="w-20 h-20 object-cover rounded-lg border border-rose-300 mr-4"
                                        />
                                        <div className="flex-grow">
                                            <p className="font-semibold">
                                                {item.variant.color || item.variant.model}
                                            </p>
                                            <p className="text-sm">Qty: {item.quantity}</p>
                                            <p className="text-sm">
                                                Price: ${item.variant.price.toFixed(2)}
                                            </p>
                                        </div>
                                        <p className="text-right">
                                            ${(item.variant.price * item.quantity).toFixed(2)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                            <h3 className="text-2xl font-bold text-right mt-4">
                                Total: ${totalPrice.toFixed(2)}
                            </h3>
                        </div>

                        {/* Checkout Form */}
                        <form onSubmit={handleCheckout} className="space-y-6">
                            <h3 className="text-2xl font-semibold mb-4">Your Details</h3>

                            {/* Input Fields */}
                            {[
                                { label: "Full Name", name: "fullName", type: "text" },
                                { label: "Email", name: "email", type: "email" },
                                { label: "Phone Number", name: "phoneNumber", type: "tel", pattern: "[0-9]{10}", placeholder: "Enter 10-digit number" },
                                { label: "Location", name: "location", type: "text" },
                                { label: "Pincode", name: "pincode", type: "text", pattern: "[0-9]{6}", placeholder: "Enter 6-digit pincode" },
                            ].map((field, index) => (
                                <div key={index}>
                                    <label className="block mb-2 text-lg">{field.label}:</label>
                                    <input
                                        type={field.type}
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        required
                                        pattern={field.pattern}
                                        placeholder={field.placeholder}
                                        className="w-full p-3 bg-black border border-rose-300 rounded-md text-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-300"
                                    />
                                </div>
                            ))}

                            {/* PayPal Button */}
                            <div className="mt-6">
                                <h3 className="text-2xl font-semibold mb-4">Pay with PayPal</h3>
                                <PayPalButtons
                                    style={{ layout: "vertical", color: "blue" }}
                                    createOrder={(data, actions) => {
                                        return actions.order.create({
                                            purchase_units: [
                                                {
                                                    amount: {
                                                        value: totalPrice.toFixed(2),
                                                    },
                                                    payee: {
                                                        email_address: "vanessaulli.finances@gmail.com",
                                                    },
                                                },
                                            ],
                                        });
                                    }}
                                    onApprove={handleApprove}
                                    fundingSource="paypal"
                                />
                            </div>

                            {/* Place Order Button */}
                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    className={`px-6 py-3 rounded-lg text-lg font-semibold transition shadow-md ${isPaid
                                        ? "bg-rose-300 text-black hover:bg-rose-400 cursor-pointer"
                                        : "bg-gray-600 text-gray-400 cursor-not-allowed"
                                        }`}
                                    disabled={!isPaid}
                                >
                                    Place Order
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default CheckoutPage;
